import { formatUtcLongDate } from '@orn/global/dates/formatUtcLongDate.js';

import { SubscriptionStatus } from '../../../__generated__/globalTypes';

/** What the plan row says, and whether the household still has access. */
export interface IBillingSummary {
  /** Short label for the current state, e.g. "Free trial". */
  planLabel: string;
  /** One sentence explaining the state and what happens next. */
  detail: string;
  /** Whether the renewal date should be read as a renewal or as an end date. */
  periodEndMeaning: 'renews' | 'endsAccess' | null;
}

/**
 * How a subscription status reads to the person paying.
 *
 * Deliberately not a status→string map: `PAST_DUE` still entitles (it is the
 * dunning grace window) but needs a different sentence from `ACTIVE`, and a
 * canceled membership keeps access until the period ends. The pairing of
 * label, sentence, and how to read the date is what makes each state legible.
 */
export function summarizeBilling(status: SubscriptionStatus, cancelAtPeriodEnd: boolean): IBillingSummary {
  if (
    cancelAtPeriodEnd &&
    (status === SubscriptionStatus.Active ||
      status === SubscriptionStatus.Trialing ||
      status === SubscriptionStatus.PastDue)
  ) {
    return {
      planLabel: 'Canceled',
      detail: 'Your subscription is canceled. You keep access until the end of the current period.',
      periodEndMeaning: 'endsAccess',
    };
  }

  switch (status) {
    case SubscriptionStatus.Trialing:
      return {
        planLabel: 'Free trial',
        detail: "You're on a free trial and haven't been charged yet.",
        periodEndMeaning: 'renews',
      };
    case SubscriptionStatus.Active:
      return {
        planLabel: 'Active',
        detail: 'Your subscription is active.',
        periodEndMeaning: 'renews',
      };
    case SubscriptionStatus.PastDue:
      return {
        planLabel: 'Payment failed',
        detail: "We couldn't collect your last payment. Update your bank account to keep your subscription.",
        periodEndMeaning: 'renews',
      };
    case SubscriptionStatus.Canceled:
      return {
        planLabel: 'Canceled',
        detail: 'Your subscription has ended.',
        periodEndMeaning: null,
      };
    case SubscriptionStatus.Paused:
      // Distinct from INCOMPLETE: a paused subscription finished setting up
      // and is deliberately not billing, so "didn't finish" would be false.
      return {
        planLabel: 'Paused',
        detail: 'Your subscription is paused and not currently billing.',
        periodEndMeaning: null,
      };
    case SubscriptionStatus.Incomplete:
      return {
        planLabel: 'Not active',
        detail: "Your subscription didn't finish setting up, so it isn't active.",
        periodEndMeaning: null,
      };
    default:
      return {
        planLabel: 'Not active',
        detail: "Your subscription isn't active.",
        periodEndMeaning: null,
      };
  }
}

/**
 * The renewal/end date as the settings card states it.
 *
 * Pinned to UTC, like the first-charge date stored on the mandate. Left to the
 * reader's timezone, a period ending at UTC midnight renders as the day before
 * for anyone west of it — so the card would name a different date than the
 * authorization the subscriber signed.
 */
export function formatPeriodEnd(date: Date): string {
  return formatUtcLongDate(date);
}
